import axios from "axios"
import qs from "qs"
// constants
import { languagesTransformer } from "i18n"
// types
import { Language } from "types/i18n"
import { Pagination } from "types/api"

export type GetEpisodeQuestionsRequest = {
  episodeId: number
  locale?: Language
}

type Question = {
  id: number
  attributes: {
    question: string
    createdAt: string
    updatedAt: string
    answer?: string
  }
}

type QuestionNormalized = {
  id: number
  question: string
  createdAt: string
  updatedAt: string
  documentId: string
  user: {
    id: number
    user_profile: {
      userFirstName: string
      userLastName: string
      userJobTitle: string
      userPicture: {
        url: string
      }
      id: number
    }
  }
  answer?: string
}

export type GetQuestionRequest = {
  documentId: string
}

export type GetQuestionResponse = {
  data: {
    id: number
    episode: {
      id: number
      documentId: string
      course: {
        documentId: string
      }
    }
  }
  meta: Pagination
}

export type GetEpisodeQuestionsResponse = QuestionNormalized[]

export type AddEpisodeQuestionRequest = {
  question: string
  episodeDocumentId: string
  locale?: Language
}

export type AddEpisodeQuestionResponse = {
  data: Question
  meta: Pagination
}

export type RemoveEpisodeQuestionRequest = {
  documentId?: string
}

export type RemoveEpisodeQuestionResponse = {
  data: Question
  meta: Pagination
}

export const QUESTIONS_KEYS = Object.freeze({
  EPISODE_QUESTIONS: "episodeQuestions",
  GET_QUESTION: "getQuestion",
})

export const getEpisodeQuestions = async ({ episodeId, locale = "en" }: GetEpisodeQuestionsRequest) => {
  const _locale = locale.slice(0, 2) as Language

  const params = qs.stringify(
    {
      locale: languagesTransformer[_locale],
      episodeId,
    },
    { encodeValuesOnly: true }
  )

  const url = `/questions?${params}`

  const response = await axios.get<GetEpisodeQuestionsResponse>(url)

  return response.data
}

export const getQuestionByDocumentId = async ({ documentId }: GetQuestionRequest) => {
  const params = qs.stringify(
    {
      fields: ["id"],
      populate: {
        episode: {
          fields: ["id"],
          populate: {
            course: {
              fields: ["id"],
            },
          },
        },
      },
    },
    { encodeValuesOnly: true }
  )

  const url = `/questions/${documentId}?${params}`

  const response = await axios.get<GetQuestionResponse>(url)

  return response.data
}

export const addEpisodeQuestion = async ({ question, episodeDocumentId, locale = "en" }: AddEpisodeQuestionRequest) => {
  const _locale = locale.slice(0, 2) as Language
  const url = `/questions`

  const response = await axios.post<AddEpisodeQuestionResponse>(url, {
    data: {
      locale: languagesTransformer[_locale],
      question,
      episode: episodeDocumentId,
    },
  })

  return response.data
}

export const removeEpisodeQuestion = async ({ documentId }: RemoveEpisodeQuestionRequest) => {
  const url = `/questions/${documentId}`

  const response = await axios.delete<RemoveEpisodeQuestionResponse>(url)

  return response.data
}
